import { Location } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { ApiResponse } from '../../../core/models/api.model';
import { Plan, PlanFormValue } from '../../../core/models/plan.model';
import { PlanService } from '../../../core/services/plan.service';
import { SummaryCardComponent } from '../../../shared/summary-card/summary-card';
import { SelectComponent, SelectOption } from '../../../shared/select/select';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import { PlanFormComponent } from './plan-form/plan-form';
import { EmpresasSuscritasComponent } from './empresas-suscritas/empresas';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import {
  ConfirmationModalComponent,
  ConfirmationStatus,
} from '../../../shared/confirmation-modal/confirmation-modal';
import { LoadingComponent } from '../../../shared/loading/loading';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { SearchInput } from '../../../shared/search-input/search-input';
import { PaginationRequest } from '../../../shared/data-table-paginado/data-table-paginado.models';

interface PlanCard {
  id: string;
  code: string;
  name: string;
  description: string;
  status: 'Activo' | 'Inactivo';
  companies: number;
  accounts: string;
  features: string[];
  highlighted?: boolean;
  characteristicsJson: string;
}

@Component({
  selector: 'app-planes',
  standalone: true,
  imports: [
    SummaryCardComponent,
    SelectComponent,
    ModalShellComponent,
    PlanFormComponent,
    EmpresasSuscritasComponent,
    UiButtonComponent,
    ConfirmationModalComponent,
    LoadingComponent,
    PageHeaderComponent,
    SearchInput,
  ],
  templateUrl: './planes.html',
  styleUrl: './planes.css',
})
export class PlanesComponent {
  private readonly location = inject(Location);
  private readonly planService = inject(PlanService);
  protected showForm = false;
  protected showEmpresas = false;
  protected showDeleteConfirmation = false;
  protected planToDelete: PlanCard | null = null;
  protected selectedPlan: PlanCard | null = null;

  protected colorIcon = 'var(--red)';
  protected backgroundIcon = 'rgba(255, 0, 0, 0.1)';

  protected deleteStatus: ConfirmationStatus = 'active';
  protected deleteMessage = '';

  protected readonly searchTerm = signal('');
  protected readonly statusFilter = signal('Activo');
  protected readonly statusOptions: SelectOption[] = [
    { label: 'Todos', value: 'Todos' },
    { label: 'Activo', value: 'Activo' },
    { label: 'Inactivo', value: 'Inactivo' },
  ];
  protected readonly loadingPage = signal(true);
  protected readonly loadingTable = signal(false);
  protected readonly statusChangingId = signal<string | null>(null);
  protected readonly deleting = signal(false);
  protected readonly loadError = signal('');
  protected readonly saveError = signal('');
  protected readonly plans = signal<PlanCard[]>([]);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly totalItems = signal(0);

  protected planEditing: PlanFormValue | null = null;

  protected readonly activePlans = computed(
    () => this.plans().filter((plan) => plan.status === 'Activo').length,
  );
  protected readonly subscribedCompanies = computed(() =>
    this.plans().reduce((total, plan) => total + plan.companies, 0),
  );
  protected readonly filteredPlans = computed(() => this.plans());

  constructor() {
    this.loadPlans();
  }

  protected loadPlans(): void {
    this.loadingTable.set(true);
    this.loadError.set('');
    const request: PaginationRequest = {
      page: this.page(),
      pageSize: this.pageSize(),
      search: this.searchTerm(),
      status: this.statusFilter(),
    };

    this.planService.getPage(request).subscribe({
      next: (plans) => {
        const result = plans.data;
        const items = result?.items ?? [];
        const highlightedPlanId = this.getHighlightedPlanId(items);
        this.plans.set(items.map((plan) => this.toPlanCard(plan, plan.id === highlightedPlanId)));
        this.totalItems.set(result?.totalItems ?? 0);
        if (result) this.page.set(result.page);
        this.loadingTable.set(false);
        this.loadingPage.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.loadError.set(
          this.getApiErrorMessage(error, 'No se pudieron cargar los planes. Intenta nuevamente.'),
        );
        this.loadingTable.set(false);
        this.loadingPage.set(false);
      },
    });
  }

  protected updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.page.set(1);
    this.loadPlans();
  }

  protected updateStatus(value: string | string[] | null): void {
    if (typeof value === 'string') {
      this.statusFilter.set(value);
      this.page.set(1);
      this.loadPlans();
    }
  }

  protected previousPage(): void {
    if (this.page() <= 1) return;
    this.page.update((page) => page - 1);
    this.loadPlans();
  }

  protected nextPage(): void {
    if (this.page() * this.pageSize() >= this.totalItems()) return;
    this.page.update((page) => page + 1);
    this.loadPlans();
  }

  protected nuevoPlan(): void {
    this.planEditing = null;
    this.saveError.set('');
    this.openForm();
  }

  protected editarPlan(plan: PlanCard): void {
    this.saveError.set('');
    this.planEditing = {
      id: plan.id,
      code: plan.code,
      name: plan.name,
      description: plan.description,
      characteristicsJson: plan.characteristicsJson,
      isActive: plan.status === 'Activo',
    };
    this.openForm();
  }

  protected toggleStatus(plan: PlanCard): void {
    this.statusChangingId.set(plan.id);
    this.planService.toggleStatus(plan.id).subscribe({
      next: () => {
        plan.status = plan.status === 'Activo' ? 'Inactivo' : 'Activo';
        this.plans.set([...this.plans()]);
        this.statusChangingId.set(null);
      },
      error: (error: HttpErrorResponse) => {
        const message = this.getApiErrorMessage(
          error,
          'No se pudo cambiar el estado del plan. Intenta nuevamente.',
        );
        alert(message);
        this.statusChangingId.set(null);
      },
    });
  }

  protected eliminarPlan(plan: PlanCard): void {
    this.planToDelete = plan;
    this.deleteStatus = 'active';
    this.deleteMessage = `Se eliminará el plan ${plan.name}. Esta acción no se puede deshacer.`;
    this.showDeleteConfirmation = true;
  }

  protected confirmarEliminarPlan(): void {
    if (!this.planToDelete) return;

    this.deleting.set(true);
    this.planService.delete(this.planToDelete.id).subscribe({
      next: () => {
        this.deleteStatus = 'success';
        this.colorIcon = 'var(--green)';
        this.backgroundIcon = 'rgba(0, 128, 0, 0.1)';
        this.deleteMessage = 'El plan fue eliminado correctamente.';
        this.loadPlans();
        this.deleting.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.deleteStatus = 'error';
        this.deleteMessage = this.getApiErrorMessage(
          error,
          'No se pudo eliminar el plan. Intenta nuevamente.',
        );
        this.deleting.set(false);
      },
    });
  }

  protected cancelarEliminarPlan(): void {
    this.closeDeleteConfirmation();
  }

  protected closeDeleteConfirmation(): void {
    this.showDeleteConfirmation = false;
    this.planToDelete = null;
    this.deleteStatus = 'active';
    this.deleteMessage = '';
    this.colorIcon = 'var(--red)';
    this.backgroundIcon = 'rgba(255, 0, 0, 0.1)';
  }

  protected onPlanSaved(value: PlanFormValue): void {
    const { id, ...request } = value;
    const operation = id ? this.planService.update(id, request) : this.planService.create(request);
    operation.subscribe({
      next: () => {
        this.saveError.set('');
        this.closeForm();
        this.loadPlans();
      },
      error: (error: HttpErrorResponse) => {
        this.saveError.set(
          this.getApiErrorMessage(error, 'No se pudo guardar el plan. Intenta nuevamente.'),
        );
      },
    });
  }

  protected verEmpresas(plan: PlanCard): void {
    this.selectedPlan = plan;
    this.showEmpresas = true;
  }

  protected volver(): void {
    this.location.back();
  }

  protected closeForm(): void {
    this.planEditing = null;
    this.saveError.set('');
    this.showForm = false;
  }

  protected openForm(): void {
    this.showForm = true;
  }

  protected closeEmpresas(): void {
    this.showEmpresas = false;
    this.selectedPlan = null;
  }

  protected openEmpresas(): void {
    this.showEmpresas = true;
  }

  private toPlanCard(plan: Plan, highlighted: boolean): PlanCard {
    const characteristics = this.parseCharacteristics(plan.characteristicsJson);
    const maxAccounts = characteristics['maxCuentas'];
    const hasAdvancedReports = characteristics['reportesAvanzados'] === true;
    const isPremium = plan.code.toUpperCase() === 'PREMIUM';
    const maxAccountsValue = typeof maxAccounts === 'number' ? maxAccounts : 0;

    return {
      id: plan.id,
      code: plan.code,
      name: plan.name,
      description: plan.description ?? 'Sin descripción disponible.',
      status: plan.isActive ? 'Activo' : 'Inactivo',
      companies: plan.subscribedCompanies,
      characteristicsJson: plan.characteristicsJson,
      accounts:
        maxAccounts === -1
          ? 'Cuentas bancarias ilimitadas'
          : `Hasta ${maxAccountsValue} cuentas bancarias`,
      features: [
        'Consulta de movimientos',
        'Proyecciones de flujo de caja',
        ...(hasAdvancedReports ? ['Reportes avanzados'] : []),
        ...(isPremium ? ['Sincronización prioritaria'] : ['Soporte estándar']),
      ],
      highlighted,
    };
  }

  private getHighlightedPlanId(plans: Plan[]): string | null {
    if (!plans.length) return null;

    const highestUsage = Math.max(...plans.map((plan) => plan.subscribedCompanies));
    const mostUsed = plans.filter((plan) => plan.subscribedCompanies === highestUsage);
    const premium = mostUsed.find((plan) => plan.code.toUpperCase() === 'PREMIUM');

    return (premium ?? mostUsed[0])?.id ?? null;
  }

  private parseCharacteristics(value: string): Record<string, unknown> {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private getApiErrorMessage(error: HttpErrorResponse, fallback: string): string {
    const response = error.error as ApiResponse<unknown> | string | null;

    if (typeof response === 'string') return response.trim() || fallback;
    if (!response) return fallback;

    if (response?.message?.trim()) return response.message;

    const firstError = response?.errors?.find((item) => item.message?.trim());
    return firstError?.message ?? fallback;
  }
}
